// vim: set ts=2 sts=2 sw=2 et:

import React from "react";

import textura from "../spectator/spectator-background.jpeg";
import styles from "./Lab.module.scss";

export type LabBackdrop = "blanco" | "textura" | "gris" | "transparente" | "imagen";

export const LAB_BACKDROPS: ReadonlyArray<{ id: LabBackdrop; label: string }> = [
  { id: "blanco", label: "Blanco (como el overlay en producción)" },
  { id: "textura", label: "Textura WRPF" },
  { id: "gris", label: "Gris medio" },
  { id: "transparente", label: "Transparente" },
  { id: "imagen", label: "Imagen propia" },
];

const CANVAS_WIDTH = 1920;
const CANVAS_HEIGHT = 1080;

const useFitScale = (): number => {
  const read = () => Math.min(window.innerWidth / CANVAS_WIDTH, window.innerHeight / CANVAS_HEIGHT);
  const [scale, setScale] = React.useState(read);
  React.useEffect(() => {
    const onResize = () => setScale(read());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return scale;
};

// Marca el body como pantalla del lab (fondo oscuro, sin la textura global de la app).
export const useLabBody = () => {
  React.useEffect(() => {
    document.body.classList.add("wrpf-lab");
    return () => document.body.classList.remove("wrpf-lab");
  }, []);
};

export interface LabZoom {
  x: number;
  y: number;
}

// `zoom=x,y` en la URL: muestra el lienzo a escala real desde ese punto, para revisar detalles.
export const parseLabZoom = (value: string | null): LabZoom | null => {
  const match = value?.match(/^(\d+),(\d+)$/);
  return match ? { x: Number(match[1]), y: Number(match[2]) } : null;
};

interface Props {
  backdrop: LabBackdrop;
  imageUrl: string | null;
  zoom?: LabZoom | null;
  children: React.ReactNode;
}

const backdropClass: Readonly<Record<LabBackdrop, string>> = {
  blanco: styles.backdropBlanco,
  textura: "",
  gris: styles.backdropGris,
  transparente: styles.backdropTransparente,
  imagen: styles.backdropImagen,
};

const LabStage = ({ backdrop, imageUrl, zoom = null, children }: Props) => {
  const scale = useFitScale();
  const transform =
    zoom === null
      ? `translate(-50%, -50%) scale(${scale})`
      : `translate(-50%, -50%) translate(${CANVAS_WIDTH / 2 - zoom.x - window.innerWidth / 2}px, ${
          CANVAS_HEIGHT / 2 - zoom.y - window.innerHeight / 2
        }px)`;
  const image = backdrop === "textura" ? textura : backdrop === "imagen" ? imageUrl : null;
  return (
    <div className={styles.viewport}>
      <div
        className={styles.canvas}
        style={{ transform }}
        aria-label="Lienzo 1920×1080"
      >
        <div className={`${styles.backdrop} ${backdropClass[backdrop]}`} aria-hidden="true">
          {image ? <img src={image} alt="" /> : null}
        </div>
        {children}
      </div>
    </div>
  );
};

export default LabStage;
